/**
 * Animation Behavior Editor
 * UI for editing Animation behavior with clip selection dropdown
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { WrapMode } from 'trace-viewer/animation';
import type { AnimationData } from 'trace-viewer/animation';
import { Animation } from 'trace-viewer/behaviors';
import { TransportButtons } from './transport-buttons';
import { AssetField } from '../components/asset-field';
import { EDITOR_EVENTS } from '../editor-events';
import type { ClipMode } from './animation-clip-picker';
import { EntityType } from 'trace-viewer/cms';
import { CatalogBrowseButton } from '../components/catalog-asset-picker';

// ============================================================================
// Shared Styles
// ============================================================================

const styles = {
    input: {
        padding: '4px 8px',
        backgroundColor: '#333',
        border: '1px solid #555',
        borderRadius: '3px',
        color: '#fff',
        fontSize: '12px',
        outline: 'none',
    } as React.CSSProperties,
    label: {
        fontSize: '12px',
        color: '#aaa',
        minWidth: '100px',
        flexShrink: 0,
    } as React.CSSProperties,
    fieldRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '8px',
    } as React.CSSProperties,
};

const tabStyle = (active: boolean): React.CSSProperties => ({
    padding: '3px 2px',
    marginRight: '8px',
    fontSize: '11px',
    border: 'none',
    borderBottom: active ? '2px solid #7ab' : '2px solid transparent',
    background: 'none',
    color: active ? '#ddd' : '#777',
    cursor: 'pointer',
});

/** Small button with CSS hover state */
const HoverButton: React.FC<{
    onClick: () => void;
    title?: string;
    style: React.CSSProperties;
    hoverBg: string;
    children: React.ReactNode;
}> = ({ onClick, title, style, hoverBg, children }) => {
    const [hovered, setHovered] = useState(false);
    return (
        <button
            onClick={onClick}
            title={title}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{ ...style, backgroundColor: hovered ? hoverBg : style.backgroundColor }}
        >
            {children}
        </button>
    );
};

// ============================================================================
// Animation Editor Component
// ============================================================================

export interface AnimationEditorProps {
    data: AnimationData;
    behavior: Animation;
    onChange: (data: AnimationData) => void;
    onCreateClip?: () => void;
    onEditClip?: () => void;
    onClipSelected?: (clipName: string) => void;
}

const WRAP_MODES = [WrapMode.Once, WrapMode.Loop, WrapMode.PingPong, WrapMode.ClampForever];

export const AnimationEditor: React.FC<AnimationEditorProps> = ({ data, behavior, onChange, onCreateClip, onEditClip, onClipSelected }) => {
    const [embeddedClipNames, setEmbeddedClipNames] = useState<Set<string>>(new Set());
    const [externalClipNames, setExternalClipNames] = useState<Set<string>>(new Set());
    const [isPlaying, setIsPlaying] = useState(false);
    const isPlayingRef = useRef(false);

    // Poll clip names at a relaxed interval (they change rarely)
    useEffect(() => {
        const update = () => {
            setEmbeddedClipNames(new Set(behavior.getEmbeddedClipNames()));
            setExternalClipNames(new Set(behavior.getExternalClipNames()));
        };
        update();
        const intervalId = setInterval(update, 500);
        return () => clearInterval(intervalId);
    }, [behavior]);

    // Track playing state via the scene render loop for instant UI updates
    useEffect(() => {
        const scene = behavior.sceneObject.babylonScene;
        const observer = scene.onBeforeRenderObservable.add(() => {
            const playing = behavior.isPlaying();
            if (playing !== isPlayingRef.current) {
                isPlayingRef.current = playing;
                setIsPlaying(playing);
            }
        });
        return () => {
            scene.onBeforeRenderObservable.remove(observer);
        };
    }, [behavior]);

    const embeddedList = [...embeddedClipNames];
    const externalList = [...externalClipNames];
    const isExternalClip = !!data.clip && externalClipNames.has(data.clip);

    // Derive initial tab from current data
    const [clipMode, setClipMode] = useState<ClipMode>(
        () => (isExternalClip || !!data.src) ? 'custom' : 'embedded'
    );

    const playClip = useCallback((clipName: string) => {
        behavior.stop();
        const loop = data.wrapMode === WrapMode.Loop;
        behavior.play({ clip: clipName, loop }).catch(err => {
            console.warn('[AnimationEditor] Failed to play clip:', err);
        });
        onClipSelected?.(clipName);
    }, [behavior, data.wrapMode, onClipSelected]);

    const handleClipChange = useCallback((value: string) => {
        onChange({ ...data, clip: value || undefined });
        if (value) {
            playClip(value);
        } else {
            behavior.stop();
        }
    }, [data, onChange, playClip, behavior]);

    const handleWrapModeChange = useCallback((wrapMode: WrapMode) => {
        onChange({ ...data, wrapMode });
        // Babylon.js play() only accepts a boolean loop flag — PingPong/ClampForever
        // map to non-looping. Restart needed since loop can't change mid-playback.
        if (behavior.isPlaying() && data.clip) {
            const loop = wrapMode === WrapMode.Loop;
            void behavior.play({ clip: data.clip, loop });
        }
    }, [data, onChange, behavior]);

    const handlePlayAutomaticallyChange = useCallback((playAutomatically: boolean) => {
        onChange({ ...data, playAutomatically });
    }, [data, onChange]);

    const handlePlayPause = useCallback(() => {
        if (behavior.isPlaying()) {
            behavior.pause();
        } else {
            const loop = data.wrapMode === WrapMode.Loop;
            void behavior.play({ clip: data.clip, loop });
        }
    }, [behavior, data.clip, data.wrapMode]);

    const handleStop = useCallback(() => {
        behavior.stop();
    }, [behavior]);

    const hasActiveClip = !!data.clip && (
        clipMode === 'embedded' ? embeddedClipNames.has(data.clip) : externalClipNames.has(data.clip)
    );

    return (
        <div>
            {/* Clip tab row */}
            <div style={styles.fieldRow}>
                <span style={styles.label}>Clip</span>
                <button onClick={() => { setClipMode('embedded'); behavior.stop(); window.dispatchEvent(new CustomEvent(EDITOR_EVENTS.CLOSE_CLIP_EDITOR)); }} style={tabStyle(clipMode === 'embedded')}>
                    Built-in{embeddedList.length > 0 ? ` (${embeddedList.length})` : ''}
                </button>
                <button onClick={() => { setClipMode('custom'); behavior.stop(); }} style={tabStyle(clipMode === 'custom')}>
                    Custom{externalList.length > 0 ? ` (${externalList.length})` : ''}
                </button>
            </div>

            {/* Clip dropdown + create button */}
            <div style={styles.fieldRow}>
                <span style={styles.label} />
                {clipMode === 'embedded' ? (
                    <select
                        value={isExternalClip ? '' : (data.clip ?? '')}
                        onChange={(e) => handleClipChange(e.target.value)}
                        style={{ ...styles.input, flex: 1, cursor: 'pointer' }}
                    >
                        <option value="">{embeddedList.length > 0 ? '(None)' : '(No clips)'}</option>
                        {embeddedList.map(name => (
                            <option key={name} value={name}>{name}</option>
                        ))}
                    </select>
                ) : (
                    <select
                        value={isExternalClip ? data.clip : ''}
                        onChange={(e) => handleClipChange(e.target.value)}
                        style={{ ...styles.input, flex: 1, cursor: 'pointer' }}
                    >
                        <option value="">{externalList.length > 0 ? '(None)' : '(No clips)'}</option>
                        {externalList.map(name => (
                            <option key={name} value={name}>{name}</option>
                        ))}
                    </select>
                )}
                {clipMode === 'custom' && onCreateClip && (
                    <HoverButton
                        onClick={onCreateClip}
                        title="Create new clip (Shift+C)"
                        hoverBg="#1e6ae0"
                        style={{
                            padding: '4px 8px',
                            backgroundColor: '#2979ff',
                            border: 'none',
                            borderRadius: '3px',
                            color: '#fff',
                            cursor: 'pointer',
                            fontSize: '12px',
                            flexShrink: 0,
                        }}
                    >
                        +
                    </HoverButton>
                )}
            </div>

            {/* Clip action buttons (edit/remove) — right-aligned with dropdown */}
            {clipMode === 'custom' && isExternalClip && (
                <div style={styles.fieldRow}>
                    <span style={styles.label} />
                    <div style={{ display: 'flex', gap: '4px', flex: 1, justifyContent: 'flex-end' }}>
                        {onEditClip && (
                            <HoverButton
                                onClick={onEditClip}
                                title="Edit selected clip (C)"
                                hoverBg="#444"
                                style={{
                                    padding: '3px 8px',
                                    backgroundColor: '#383838',
                                    border: '1px solid #555',
                                    borderRadius: '3px',
                                    color: '#ccc',
                                    cursor: 'pointer',
                                    fontSize: '11px',
                                }}
                            >
                                Edit
                            </HoverButton>
                        )}
                        <HoverButton
                            onClick={() => {
                                if (data.clip) behavior.removeExternalClip(data.clip);
                                onChange({ ...data, clip: undefined, src: undefined, target: undefined });
                                window.dispatchEvent(new CustomEvent(EDITOR_EVENTS.CLOSE_CLIP_EDITOR));
                            }}
                            title="Delete selected clip and remove source"
                            hoverBg="#444"
                            style={{
                                padding: '3px 8px',
                                backgroundColor: '#383838',
                                border: '1px solid #555',
                                borderRadius: '3px',
                                color: '#c66',
                                cursor: 'pointer',
                                fontSize: '11px',
                            }}
                        >
                            Delete
                        </HoverButton>
                    </div>
                </div>
            )}

            {/* Custom-only: source + target */}
            {clipMode === 'custom' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <AssetField
                            label="Source"
                            value={data.src ?? ''}
                            onChange={(src) => onChange({ ...data, src: src || undefined })}
                            assetType="model"
                            resources={behavior.sceneObject.resources}
                        />
                    </div>
                    <CatalogBrowseButton
                        entityType={EntityType.Animation}
                        onSelect={(asset) => onChange({ ...data, src: asset.url })}
                        title="Browse catalog animations"
                    />
                </div>
            )}
            {clipMode === 'custom' && data.src && (
                <div style={styles.fieldRow}>
                    <span style={styles.label}>Target</span>
                    <select
                        value={data.target ?? '@vroot'}
                        onChange={(e) => onChange({ ...data, target: e.target.value as '@self' | '@vroot' })}
                        style={{ ...styles.input, flex: 1, cursor: 'pointer' }}
                    >
                        <option value="@vroot">@vroot (animation root)</option>
                        <option value="@self">@self (object transform)</option>
                    </select>
                </div>
            )}

            {/* Separator after custom section */}
            {clipMode === 'custom' && (
                <div style={{ borderTop: '1px solid #3a3a3a', marginBottom: '8px' }} />
            )}

            {/* Playback Controls — only when a clip is selected for the active tab */}
            {hasActiveClip && (
                <div style={styles.fieldRow}>
                    <span style={styles.label}>Preview</span>
                    <TransportButtons
                        isPlaying={isPlaying}
                        onPlayPause={handlePlayPause}
                        onStop={handleStop}
                    />
                </div>
            )}

            {/* Wrap Mode */}
            <div style={styles.fieldRow}>
                <span style={styles.label}>Wrap Mode</span>
                <select
                    value={data.wrapMode ?? 'Default'}
                    onChange={(e) => handleWrapModeChange(e.target.value as WrapMode)}
                    style={{ ...styles.input, flex: 1, cursor: 'pointer' }}
                >
                    {WRAP_MODES.map((mode) => (
                        <option key={mode} value={mode}>{mode}</option>
                    ))}
                </select>
            </div>

            {/* Play Automatically */}
            <div style={styles.fieldRow}>
                <span style={styles.label}>Play Automatically</span>
                <input
                    type="checkbox"
                    checked={data.playAutomatically ?? true}
                    onChange={(e) => handlePlayAutomaticallyChange(e.target.checked)}
                    style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                />
            </div>
        </div>
    );
};
